import { useEffect, useState } from 'react';
import { List, Spin, Empty, Typography, Button, App, Tooltip } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { notificationService } from '@/services/notificationService';
import { Notification } from '@/types/notification';
import { NotificationItem } from './NotificationItem';
import { useDeeplink } from '@/hooks/useDeeplink';
import './NotificationItem.css';

const { Text } = Typography;

interface NotificationRowProps {
  notification: Notification;
  onNotificationRead?: (notification: Notification) => void;
  onClose?: () => void;
}

function NotificationRow({ notification, onNotificationRead, onClose }: NotificationRowProps) {
  const { navigateToDeeplink, isLoading, error } = useDeeplink(notification);
  const isObsolete = Boolean(notification.is_obsolete ?? notification.deleted_at);

  const handleClick = async () => {
    // If not already read, mark as read before navigating
    if (!notification.is_read) {
      onNotificationRead?.(notification);
    }
    // Close the dropdown before navigating
    onClose?.();
    // Always navigate to the deeplink
    navigateToDeeplink();
  };

  // Don't render error boundary for obsolete notifications - just show visual indicator
  return (
    <NotificationItem
      notification={notification}
      onClick={handleClick}
      isLoading={isLoading}
      isObsolete={isObsolete || Boolean(error)}
    />
  );
}

interface NotificationDropdownProps {
  onNotificationsUpdated?: () => void;
  onClose?: () => void;
}

export function NotificationDropdown({ onNotificationsUpdated, onClose }: NotificationDropdownProps = {}) {
  const { message } = App.useApp();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await notificationService.getNotifications({ page: 1, limit: 50 });
        if (active) setNotifications(data || []);
      } catch (error) {
        console.error('[NotificationDropdown] failed to load notifications', error);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const handleNotificationRead = async (notification: Notification) => {
    try {
      // Call API to mark as read
      await notificationService.markAsRead(notification.id);
      
      // Update local state immediately
      setNotifications(prev =>
        prev.map(n =>
          n.id === notification.id ? { ...n, is_read: true } : n
        )
      );
      
      // Notify parent to refresh unread count in header
      onNotificationsUpdated?.();
    } catch (error) {
      console.error('[NotificationDropdown] failed to mark notification as read', error);
      // Don't show error message for this action - it's secondary to navigation
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) {
      message.info('Todas as notificações já estão marcadas como lidas');
      return;
    }

    setMarkingAsRead(true);
    try {
      await notificationService.markAllAsRead();
      // Update local state to mark all as read
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      message.success('Todas as notificações foram marcadas como lidas');
      // Notify parent component to refresh unread count
      onNotificationsUpdated?.();
    } catch (error) {
      console.error('[NotificationDropdown] failed to mark all as read', error);
      message.error('Erro ao marcar notificações como lidas');
    } finally {
      setMarkingAsRead(false);
    }
  };

  const handleDismissRead = async () => {
    const readCount = notifications.filter(n => n.is_read).length;
    if (readCount === 0) {
      message.info('Nenhuma notificação lida para limpar');
      return;
    }

    try {
      await notificationService.dismissRead();
      setNotifications(prev => prev.filter(n => !n.is_read));
      onNotificationsUpdated?.();
    } catch (error) {
      console.error('[NotificationDropdown] failed to dismiss read notifications', error);
      message.error('Erro ao limpar notificações lidas');
    }
  };

  if (loading) {
    return (
      <div className="notification-dropdown-container">
        <Spin style={{ width: '100%' }} />
      </div>
    );
  }

  if (!notifications.length) {
    return (
      <div className="notification-dropdown-container">
        <Empty description="Nenhuma notificação" />
      </div>
    );
  }

  return (
    <div className="notification-dropdown-container">
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong>Notificações</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {unreadCount > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleMarkAllAsRead}
              loading={markingAsRead}
            >
              Marcar todos como lido
            </Button>
          )}
          <Tooltip title="Limpar notificações lidas">
            <Button
              type="text"
              size="small"
              icon={<ClearOutlined />}
              onClick={handleDismissRead}
              disabled={!notifications.some(n => n.is_read)}
            />
          </Tooltip>
        </div>
      </div>
      <List
        dataSource={notifications}
        renderItem={(item) => <NotificationRow notification={item} onNotificationRead={handleNotificationRead} onClose={onClose} />}
      />
    </div>
  );
}
